import React, { useMemo, useState } from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    Divider,
} from '@mui/material';
import {
    Close,
    Download,
    SortByAlpha,
    SortOutlined,
    Percent,
    FilterAltOutlined,
    ArrowDropDown,
    Pin,
    DocumentScanner,
    Timeline,
} from '@mui/icons-material';
import { useBoolean } from 'react-hanger';
import { format } from 'date-fns';
import { track } from 'c-lib';
import type { ChartExportProps } from './types';
import ChartExport from './ChartExport';

export type AllowedAction =
    | 'download'
    | 'sortAsc'
    | 'sortDesc'
    | 'sortAlpha'
    | 'togglePercent'
    | 'toggleNumbers'
    | 'toggleAccumulate'
    | 'filter'
    | 'exportData';

type Props = Omit<ChartExportProps & { startDate: Date; endDate: Date }, 'closeBtn'> & {
    allowedActions?: AllowedAction[];
    onSortAsc?: () => void;
    onSortDesc?: () => void;
    onSortAlpha?: () => void;
    onTogglePercent?: () => void;
    onToggleNumbers?: () => void;
    onToggleAccumulate?: () => void;
    onFilterChange?: (value: 'Expected' | 'Delivered' | 'Both') => void;
    onExportData?: (format: 'json' | 'csv') => void;
    activeSort?: 'asc' | 'desc' | 'alpha' | null;
    percentOn?: boolean;
    numbersOn?: boolean;
    accumulateOn?: boolean;
    activeFilter?: 'Expected' | 'Delivered' | 'Both';
    filterOptionLabels?: {
        expected: string;
        delivered: string;
        both: string;
    };
};

const ChartWrapperControls: React.FC<Props> = ({
    type,
    chartProps,
    tableProps,
    namePrefix,
    startDate,
    endDate,
    allowedActions = ['download'],
    onSortAsc,
    onSortDesc,
    onSortAlpha,
    onTogglePercent,
    onToggleNumbers,
    onToggleAccumulate,
    onFilterChange,
    onExportData,
    activeSort,
    percentOn = false,
    numbersOn = false,
    accumulateOn = true,
    activeFilter,
    filterOptionLabels,
}) => {
    const open = useBoolean(false);
    const closeButton = useMemo(
        () => (
            <IconButton onClick={open.setFalse}>
                <Close />
            </IconButton>
        ),
        [open.setFalse],
    );
    const reportName = useMemo(
        () => `${namePrefix}_${format(startDate, 'yyyy-MM-dd')}___${format(endDate, 'yyyy-MM-dd')}`,
        [namePrefix, startDate, endDate],
    );
    const chartDialogComponent = useMemo(
        () => (
            <ChartExport
                type={type}
                chartProps={chartProps}
                tableProps={tableProps}
                closeBtn={closeButton}
                namePrefix={reportName}
            />
        ),
        [chartProps, type, tableProps, closeButton, reportName],
    );

    const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
    const [localFilter, setLocalFilter] = useState<'Expected' | 'Delivered' | 'Both'>('Both');
    const filter = activeFilter ?? localFilter;
    const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);

    const has = (k: AllowedAction) => allowedActions.includes(k);
    const filterLabels = useMemo(
        () => ({
            Expected: filterOptionLabels?.expected ?? 'Expected',
            Delivered: filterOptionLabels?.delivered ?? 'Delivered',
            Both: filterOptionLabels?.both ?? 'Both',
        }),
        [filterOptionLabels],
    );

    const activeStyle = (isActive: boolean) =>
        isActive
            ? {
                  color: 'primary.main',
                  bgcolor: 'action.selected',
                  '&:hover': { bgcolor: 'action.selected' },
                  borderRadius: 1,
              }
            : undefined;

    const percentDisabled = filter === 'Expected';

    const handleTogglePercent = () => {
        if (percentDisabled) return;
        const turningOn = !percentOn;
        if (turningOn && numbersOn) {
            onToggleNumbers?.();
        }
        onTogglePercent?.();
    };

    const handleToggleNumbers = () => {
        const turningOn = !numbersOn;
        if (turningOn && percentOn) {
            onTogglePercent?.();
        }
        onToggleNumbers?.();
    };

    const handlePickFilter = (opt: 'Expected' | 'Delivered' | 'Both') => {
        setLocalFilter(opt);
        onFilterChange?.(opt);
        setFilterAnchor(null);
        if (opt === 'Expected' && percentOn) {
            onTogglePercent?.();
        }
    };

    const handleExportClick = (fmt: 'csv' | 'json') => {
        track('export_chart_data', { format: fmt });
        onExportData?.(fmt);
        setExportAnchor(null);
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {has('sortDesc') && (
                <Tooltip title="Sort Descending">
                    <IconButton
                        size="small"
                        onClick={onSortDesc}
                        aria-pressed={activeSort === 'desc'}
                        sx={activeStyle(activeSort === 'desc')}
                    >
                        <SortOutlined fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}

            {has('sortAsc') && (
                <Tooltip title="Sort Ascending">
                    <IconButton
                        size="small"
                        onClick={onSortAsc}
                        aria-pressed={activeSort === 'asc'}
                        sx={activeStyle(activeSort === 'asc')}
                    >
                        <SortOutlined sx={{ transform: 'scaleY(-1)' }} fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            {has('sortAlpha') && (
                <Tooltip title="Sort Alphabetically">
                    <IconButton
                        size="small"
                        onClick={onSortAlpha}
                        aria-pressed={activeSort === 'alpha'}
                        sx={activeStyle(activeSort === 'alpha')}
                    >
                        <SortByAlpha fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}

            {has('togglePercent') && (
                <Tooltip
                    title={
                        percentDisabled
                            ? `Percentage unavailable when filtering ${filterLabels.Expected}`
                            : percentOn
                            ? 'Hide % from chart'
                            : 'Add % to chart'
                    }
                >
                    <span>
                        <IconButton
                            size="small"
                            onClick={handleTogglePercent}
                            aria-pressed={!!percentOn}
                            disabled={percentDisabled}
                            sx={activeStyle(!percentDisabled && !!percentOn)}
                        >
                            <Percent fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            )}

            {has('toggleNumbers') && (
                <Tooltip title={numbersOn ? 'Hide numbers' : 'Add numbers to chart'}>
                    <IconButton
                        size="small"
                        onClick={handleToggleNumbers}
                        aria-pressed={!!numbersOn}
                        sx={activeStyle(!!numbersOn)}
                    >
                        <Pin fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}

            {has('toggleAccumulate') && (
                <Tooltip
                    title={
                        accumulateOn
                            ? 'Showing running total — click for per-day'
                            : 'Showing per-day — click for running total'
                    }
                >
                    <IconButton
                        size="small"
                        onClick={onToggleAccumulate}
                        aria-pressed={!!accumulateOn}
                        sx={activeStyle(!!accumulateOn)}
                    >
                        <Timeline fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}

            {has('filter') && (
                <>
                    <Tooltip title={`View: ${filterLabels.Expected} / ${filterLabels.Delivered}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton
                                size="small"
                                onClick={e => setFilterAnchor(e.currentTarget)}
                                aria-haspopup="menu"
                                aria-expanded={Boolean(filterAnchor)}
                                aria-label="Filter series"
                                sx={activeStyle(filter !== 'Both')}
                            >
                                <FilterAltOutlined fontSize="small" />
                                <ArrowDropDown fontSize="small" />
                            </IconButton>

                            <Box
                                sx={{
                                    ml: 0.5,
                                    px: 0.75,
                                    py: 0.25,
                                    fontSize: 12,
                                    borderRadius: 1,
                                    bgcolor: 'action.selected',
                                    color: 'text.primary',
                                    display: filter === 'Both' ? 'none' : 'inline-flex',
                                    alignItems: 'center',
                                    lineHeight: 1.2,
                                }}
                            >
                                {filterLabels[filter]}
                            </Box>
                        </Box>
                    </Tooltip>

                    <Menu
                        anchorEl={filterAnchor}
                        open={Boolean(filterAnchor)}
                        onClose={() => setFilterAnchor(null)}
                    >
                        {(['Expected', 'Delivered', 'Both'] as const).map(opt => (
                            <MenuItem
                                key={opt}
                                selected={filter === opt}
                                onClick={() => handlePickFilter(opt)}
                            >
                                {filterLabels[opt]}
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            )}
            {has('exportData') && (
                <>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    <Tooltip title="Download data (CSV / JSON)">
                        <IconButton
                            size="small"
                            onClick={e => setExportAnchor(e.currentTarget)}
                            aria-haspopup="menu"
                            aria-expanded={Boolean(exportAnchor)}
                        >
                            <DocumentScanner fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={exportAnchor}
                        open={Boolean(exportAnchor)}
                        onClose={() => setExportAnchor(null)}
                    >
                        <MenuItem onClick={() => handleExportClick('csv')}>Download CSV</MenuItem>
                        <MenuItem onClick={() => handleExportClick('json')}>Download JSON</MenuItem>
                    </Menu>
                </>
            )}

            {has('download') && (
                <>
                    <Tooltip title="Download graph (configuration)">
                        <IconButton onClick={open.setTrue} size="small">
                            <Download fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                    {open.value && (
                        <Dialog
                            open={open.value}
                            onClose={open.setFalse}
                            fullScreen
                            PaperProps={{ sx: { width: '100vw', height: '100vh' } }}
                        >
                            <DialogContent sx={{ p: 2, height: '100%' }}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    {chartDialogComponent}
                                </Box>
                            </DialogContent>
                        </Dialog>
                    )}
                </>
            )}
        </Box>
    );
};

export default ChartWrapperControls;
